import React, { useEffect, useState } from 'react';
import { Target, Plus, Loader2, AlertTriangle } from 'lucide-react';
import type {
  TaskListResponse,
  TaskStatus,
  GoalTemplateDetail,
  NextEpisodeResponse,
  PatientGoalDetail,
} from '../../services/api/types.gen';
import type { GoalTaskCapabilities } from '../../utils/goalTaskCapabilities';
import type { DisciplineType } from '../../services/api/models/DisciplineType';

import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  Tabs,
  TabsList,
  TabsTrigger,
  TabsContent,
  Button,
} from '@roger/ui-components';
import {
  getGoalTemplatesForCreation,
  getNextEpisodeForEpisode,
  getPatientGoalsForVisit,
} from '../../services/api';
import { useToast } from '../../hooks/useToastContext';
import AddGoalsBox from './AddGoalsBox';
import CreateGoalFromTemplateCard from './CreateGoalFromTemplateCard';
import DisplayGoalCard from './DisplayGoalCard';
import { EDITABLE_STATUSES } from '../../constants/statuses';
import { useClosedEpisodeLock } from '../../utils/closedEpisodeLock';
import { STATUS_DISPLAY_LABELS } from '../task/taskStatus';

/**
 * The visit fields GoalsTab actually reads. Narrowed from TaskListResponse so
 * callers that only have these fields (e.g. the embedded OASIS visit form) can
 * supply them without fabricating the rest. A full TaskListResponse still
 * satisfies this shape.
 */
export type GoalsTabVisit = Pick<
  TaskListResponse,
  'id' | 'visit_type' | 'episode_start_date' | 'episode_end_date'
> & {
  status?: TaskStatus;
  /** True when the visit's period is closed (EMR-1859). */
  episode_closed?: boolean | null;
  /**
   * Episode the visit belongs to. Optional because some embedding contexts
   * (e.g. the OASIS form) may not always have it wired through; when absent,
   * the push-to-next-episode control is treated as having no next episode
   * (disabled) rather than fetching against an unknown id.
   */
  episode_id?: string;
};

interface GoalsTabProps {
  open: boolean;
  onClose: () => void;
  patientFirstName: string;
  patientLastName: string;
  visit: GoalsTabVisit;
  inline?: boolean;
  /**
   * What this task's Goals surface may do. Computed by the caller —
   * `visitGoalCapabilities`/`orderGoalCapabilities` in
   * `utils/goalTaskCapabilities.ts` — rather than derived here, so GoalsTab
   * itself stays task-shape-agnostic and every caller (visit tab, embedded
   * OASIS section, order detail panel) reads the same gating logic.
   */
  capabilities: GoalTaskCapabilities;
  /**
   * Required when `visit.id` refers to an order: the goal-template pick-list
   * endpoints 400 without a discipline for an order task (EMR-1536). Ignored
   * for a real visit, which is already discipline-scoped server-side. Also
   * used to bucket the active-goals list into "«discipline» Goals" / "Other
   * Disciplines" groups when supplied.
   */
  discipline?: DisciplineType;
  /**
   * Called after the goal set changes (add a goal, or mark one met/unattainable
   * or delete it — all of which flow through the DisplayGoalCard funnel). The
   * parent uses this to refresh its cached active-goal count so the tab badge
   * stays correct without waiting for a tab switch (ROG-3196). A progress-note or
   * target-date edit routes through the same funnel and fires too; that's a
   * harmless extra refetch since the parent recomputes from server truth.
   */
  onGoalsChanged?: () => void;
}

const EmptyState: React.FC<{ message: string }> = ({ message }) => (
  <div className='flex flex-col items-center justify-center py-12 text-muted-foreground'>
    <Target className='h-12 w-12 mb-4 opacity-30' />
    <p className='text-base font-medium'>{message}</p>
  </div>
);

/**
 * One discipline-grouped section of the active-goals grid (order mode only).
 * Renders nothing when the group is empty so an order with goals in only one
 * discipline doesn't show a hollow "Other Disciplines" header.
 */
const GoalGroup: React.FC<{
  title: string;
  goals: PatientGoalDetail[];
  renderGoal: (goal: PatientGoalDetail) => React.ReactNode;
}> = ({ title, goals, renderGoal }) => {
  if (goals.length === 0) return null;
  return (
    <div className='space-y-2'>
      <h4 className='text-xs font-semibold uppercase tracking-wide text-muted-foreground'>
        {title}
      </h4>
      <div className='grid grid-cols-1 xl:grid-cols-2 items-start gap-2'>
        {goals.map(renderGoal)}
      </div>
    </div>
  );
};

const GoalsTab: React.FC<GoalsTabProps> = ({
  open,
  onClose,
  patientFirstName,
  patientLastName,
  visit,
  inline = false,
  capabilities,
  discipline,
  onGoalsChanged,
}) => {
  const [isAddGoalsOpen, setIsAddGoalsOpen] = useState(false);
  // Each pending card remembers the discipline it was picked under (an
  // order's dropdown value at that moment), so switching the dropdown later
  // does not retag goals already being added (EMR-1661). Unset for a visit.
  const [freeTextGoalKeys, setFreeTextGoalKeys] = useState<
    Array<{ key: string; discipline?: DisciplineType }>
  >([]);
  const [templatesFromCreation, setTemplatesFromCreation] = useState<
    Array<{ template: GoalTemplateDetail; discipline?: DisciplineType }>
  >([]);
  const [activeGoals, setActiveGoals] = useState<PatientGoalDetail[]>([]);
  const [activeTab, setActiveTab] = useState<'active' | 'met' | 'unattainable'>(
    'active'
  );
  const [isLoading, setIsLoading] = useState(false);
  // Next-episode info backs the "Push to next episode" goal action
  // (EMR-1381). Fetched once per episode here rather than per goal card,
  // since a single episode can have many goal cards. `undefined` means "not
  // yet fetched / unknown episode" (treated as no next episode for gating
  // purposes), `null` means "fetched, no next episode exists".
  const [nextEpisode, setNextEpisode] = useState<
    NextEpisodeResponse | null | undefined
  >(undefined);
  const { error: showError } = useToast();

  // Resets all component state when the Goals tab dialog is closed. IMPORTANT:
  // When adding new state variables, add them to this function so the component
  // starts fresh each time it's opened.
  const resetGoalsTabState = () => {
    setIsAddGoalsOpen(false);
    setFreeTextGoalKeys([]);
    setTemplatesFromCreation([]);
    setActiveGoals([]);
    setActiveTab('active');
    setIsLoading(false);
    setNextEpisode(undefined);
  };

  useEffect(() => {
    if (!open) {
      resetGoalsTabState();
    }
  }, [open]);

  useEffect(() => {
    if (!open) return;
    setIsLoading(true);
    getPatientGoalsForVisit({
      path: { visit_id: visit.id },
      throwOnError: true,
    })
      .then(response => {
        setActiveGoals(response.data ?? []);
      })
      .catch(() => {
        showError(
          'Error loading goals',
          'There was an error loading goals for this visit.'
        );
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, [open, visit.id]);

  // Fetches next-episode info once per episode (not per goal card — a single
  // episode can have many goal cards) to back the "Push to next episode" goal
  // action (EMR-1381). A missing episode_id (some embedding contexts don't
  // wire it through) is treated as "no next episode known" for gating.
  useEffect(() => {
    if (!open || !visit.episode_id) {
      setNextEpisode(null);
      return;
    }
    const episodeId = visit.episode_id;
    setNextEpisode(undefined);
    getNextEpisodeForEpisode({
      path: { episode_id: episodeId },
      throwOnError: true,
    })
      .then(response => {
        // The generated client coerces a legitimate `null` JSON body (this
        // endpoint's documented "no next episode" response) into `{}` — see
        // `frontend/src/services/api/client/client.ts`'s `data: data ?? {}`.
        // An `id`-less object is that coercion, not a real episode.
        const episode = response.data;
        setNextEpisode(episode && 'id' in episode ? episode : null);
      })
      .catch(error => {
        // The push action renders disabled, but a silent failure would let
        // the menu claim "the next episode hasn't been created yet" when it
        // may exist — tell the user something actually went wrong.
        console.error(error);
        showError(
          'Unable to check for a next episode',
          'Please close and reopen Goals to try again. Contact support if this keeps happening.'
        );
        setNextEpisode(null);
      });
  }, [open, visit.episode_id]);

  const handleAddGoals = (goalTemplateIds: string[]) => {
    if (goalTemplateIds.length === 0) return;
    getGoalTemplatesForCreation({
      path: { visit_id: visit.id },
      query: discipline ? { discipline } : undefined,
      body: goalTemplateIds,
      throwOnError: true,
    })
      .then(response => {
        const newTemplates = response.data ?? [];
        setTemplatesFromCreation(prev => {
          const existingIds = new Set(prev.map(t => t.template.id));
          return [
            ...prev,
            ...newTemplates
              .filter(t => !existingIds.has(t.id))
              .map(template => ({ template, discipline })),
          ];
        });
      })
      .catch(() => {
        showError(
          'Error selecting templates',
          'There was an error selecting those templates.'
        );
      });
  };

  const handleGoalDiscarded = (templateId: string) => {
    setTemplatesFromCreation(prev =>
      prev.filter(t => t.template.id !== templateId)
    );
  };

  const handleGoalSaved = (templateId: string) => {
    setTemplatesFromCreation(prev =>
      prev.filter(t => t.template.id !== templateId)
    );
    getPatientGoalsForVisit({
      path: { visit_id: visit.id },
      throwOnError: true,
    })
      .then(response => {
        setActiveGoals(response.data ?? []);
        onGoalsChanged?.();
      })
      .catch(() => {
        showError('Failed to load goals');
      });
  };

  const handleGoalProgressSaved = (updatedGoals: PatientGoalDetail[]) => {
    setActiveGoals(updatedGoals);
    onGoalsChanged?.();
  };

  const handleTabChange = (value: string) => {
    setActiveTab(value as 'active' | 'met' | 'unattainable');
  };

  const handleOpenAddGoals = () => {
    setIsAddGoalsOpen(true);
  };

  const handleCloseAddGoals = () => {
    setIsAddGoalsOpen(false);
  };

  const handleUseFreeText = () => {
    setFreeTextGoalKeys(prev => [
      ...prev,
      { key: `free-text-${Date.now()}`, discipline },
    ]);
  };

  const handleCancelFreeTextGoal = (key: string) => {
    setFreeTextGoalKeys(prev => prev.filter(entry => entry.key !== key));
  };

  const handleFreeTextGoalSaved = (key: string) => {
    setFreeTextGoalKeys(prev => prev.filter(entry => entry.key !== key));
    getPatientGoalsForVisit({
      path: { visit_id: visit.id },
      throwOnError: true,
    })
      .then(response => {
        setActiveGoals(response.data ?? []);
        onGoalsChanged?.();
      })
      .catch(() => {
        showError('Failed to load goals');
      });
  };

  const handleSaveForTemplate = (templateId: string) => () => {
    handleGoalSaved(templateId);
  };

  const handleCancelForTemplate = (templateId: string) => () => {
    handleGoalDiscarded(templateId);
  };

  // Any status outside `EDITABLE_STATUSES` (not_started/saved/submitted/
  // reopened) renders the view-only styling/banner below, for both a visit
  // and an order. That's correct for an order because every status in
  // `LOCKED_ORDER_PHYSICIAN_STATUSES` is outside `EDITABLE_STATUSES` (the two
  // sets don't overlap), so a locked order always gets the banner too — it's
  // just not the only thing that can trigger it. Which actions are actually
  // offered is decided separately by `capabilities` (from
  // `orderGoalCapabilities`/`visitGoalCapabilities`, which already fold
  // `isOrderGoalLocked` in), not by this flag. A missing `status` (some
  // embedding contexts may not wire it through) is treated as valid rather
  // than blocking the whole section.
  const { locked: closedEpisodeLocked } = useClosedEpisodeLock(
    visit.episode_closed
  );
  const isValidStateForEditing =
    (!visit.status || EDITABLE_STATUSES.has(visit.status)) &&
    !closedEpisodeLocked;

  // Bucket priority mirrors the backend Goal Summary elif order (build_print_view_data
  // in goals_print_view.py): is_met wins, then is_unattainable, else active. Extended
  // goals stay in the Active tab (product decision): the clinician can still track and
  // edit progress on them here, so hiding them read as the goal disappearing. The
  // "Extended to next episode" chip on the card carries the state instead.
  const metGoals = activeGoals.filter(goal => goal.is_met ?? false);
  const unattainableGoals = activeGoals.filter(
    goal => !(goal.is_met ?? false) && (goal.is_unattainable ?? false)
  );
  const activeGoalsFiltered = activeGoals.filter(
    goal => !(goal.is_met ?? false) && !(goal.is_unattainable ?? false)
  );

  // Discipline grouping (orders only — `discipline` is unset for a visit,
  // which is already discipline-scoped server-side and renders one flat
  // list). A goal belongs to the selected discipline's group when its
  // template's `disciplines` includes it; a custom goal (no template, empty
  // `disciplines`) is attributed to the selected discipline too, since it was
  // created while that discipline was selected.
  const groupBySelectedDiscipline = (
    goals: PatientGoalDetail[]
  ): { selected: PatientGoalDetail[]; other: PatientGoalDetail[] } => {
    if (!discipline) return { selected: goals, other: [] };
    const selected: PatientGoalDetail[] = [];
    const other: PatientGoalDetail[] = [];
    for (const goal of goals) {
      const goalDisciplines = goal.disciplines ?? [];
      if (
        goalDisciplines.length === 0 ||
        goalDisciplines.includes(discipline)
      ) {
        selected.push(goal);
      } else {
        other.push(goal);
      }
    }
    return { selected, other };
  };

  const activeGoalGroups = groupBySelectedDiscipline(activeGoalsFiltered);

  const renderActiveGoalCard = (goal: PatientGoalDetail) => (
    <DisplayGoalCard
      key={goal.id}
      goal={goal}
      visitId={visit.id}
      isViewOnly={!isValidStateForEditing}
      canTrackProgress={capabilities.canRecordProgress}
      canAddInterventions={capabilities.canAddInterventions}
      canMarkGoalOutcome={capabilities.canMarkGoalOutcome}
      canEditGoal={capabilities.canEditGoals}
      canDeleteGoal={capabilities.canDeleteGoals}
      canPushGoal={capabilities.canPushGoals}
      canPerformInterventions={capabilities.canPerformInterventions}
      canCompleteInterventions={capabilities.canCompleteInterventions}
      canDeleteInterventions={capabilities.canDeleteGoals}
      episodeStartDate={visit.episode_start_date}
      episodeEndDate={visit.episode_end_date}
      nextEpisode={nextEpisode ?? null}
      onGoalProgressSaved={handleGoalProgressSaved}
    />
  );

  const start = visit.episode_start_date;
  const end = visit.episode_end_date;
  const hasEpisodeDates = start && end;
  const showCreationCards =
    (templatesFromCreation.length > 0 || freeTextGoalKeys.length > 0) &&
    hasEpisodeDates;
  const showEpisodeDateError =
    (templatesFromCreation.length > 0 || freeTextGoalKeys.length > 0) &&
    !hasEpisodeDates;

  const content = (
    <div className={inline ? 'flex flex-col h-full min-h-[500px]' : 'contents'}>
      {!inline && (
        <DialogHeader>
          <DialogTitle className='flex items-center gap-2'>
            <Target className='h-5 w-5' />
            Goals for {patientFirstName} {patientLastName}
          </DialogTitle>
          <DialogDescription>
            Patient care goals and objectives
          </DialogDescription>
        </DialogHeader>
      )}
      <Tabs
        value={activeTab}
        onValueChange={handleTabChange}
        className='flex flex-col flex-1 min-h-0'
      >
        <div className='mb-3 flex flex-wrap items-center gap-2'>
          <TabsList className='h-auto w-full flex-wrap rounded-lg bg-muted/40 p-1 sm:h-9 sm:w-fit sm:flex-nowrap'>
            <TabsTrigger
              value='active'
              className='h-7 min-w-0 whitespace-normal px-3 text-xs leading-tight sm:text-sm'
            >
              Active ({activeGoalsFiltered.length})
            </TabsTrigger>
            <TabsTrigger
              value='met'
              className='h-7 min-w-0 whitespace-normal px-3 text-xs leading-tight sm:text-sm'
            >
              Met ({metGoals.length})
            </TabsTrigger>
            <TabsTrigger
              value='unattainable'
              className='h-7 min-w-0 whitespace-normal px-3 text-xs leading-tight sm:text-sm'
            >
              Unattainable ({unattainableGoals.length})
            </TabsTrigger>
          </TabsList>
          {!isValidStateForEditing && (
            <div className='inline-flex items-center gap-1.5 rounded-md border border-amber-200 bg-amber-50 px-2.5 py-1 text-xs text-amber-800'>
              <AlertTriangle className='h-3.5 w-3.5 flex-shrink-0 text-amber-600' />
              {closedEpisodeLocked
                ? 'This episode is closed. Amending it needs permission.'
                : `Editing goals is unavailable because this task is ${
                    visit.status
                      ? (STATUS_DISPLAY_LABELS[visit.status] ?? visit.status)
                      : 'not in an editable state'
                  }.`}
            </div>
          )}
          {isValidStateForEditing &&
            activeTab === 'active' &&
            capabilities.canAddGoals && (
              <Button
                size='sm'
                onClick={handleOpenAddGoals}
                className='flex items-center gap-1'
              >
                <Plus className='h-4 w-4' />
                Add Goals
              </Button>
            )}
        </div>
        <TabsContent
          value='active'
          className='data-[state=active]:flex flex-col data-[state=active]:flex-1 min-h-0'
        >
          <div className='overflow-y-auto flex-1 pr-2 [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden'>
            {isLoading ? (
              <div className='flex items-center justify-center py-12 text-muted-foreground'>
                <Loader2 className='h-6 w-6 animate-spin' />
              </div>
            ) : showEpisodeDateError ? (
              <EmptyState message='Episode dates are required to add goals. Please refresh the page.' />
            ) : (
              <div className='space-y-2'>
                {showCreationCards && (
                  <div className='space-y-2 rounded-md border border-primary/20 bg-primary/5 p-2'>
                    <p className='text-xs font-medium text-primary'>
                      Goals currently being added
                    </p>
                    <div className='grid grid-cols-1 xl:grid-cols-2 items-start gap-2'>
                      {freeTextGoalKeys.map(entry => (
                        <CreateGoalFromTemplateCard
                          key={entry.key}
                          template={null}
                          episodeStartDate={start}
                          episodeEndDate={end}
                          visitId={visit.id}
                          discipline={entry.discipline}
                          onSave={() => handleFreeTextGoalSaved(entry.key)}
                          onCancel={() => handleCancelFreeTextGoal(entry.key)}
                        />
                      ))}
                      {templatesFromCreation.map(entry => (
                        <CreateGoalFromTemplateCard
                          key={entry.template.id}
                          template={entry.template}
                          episodeStartDate={start}
                          episodeEndDate={end}
                          visitId={visit.id}
                          discipline={entry.discipline}
                          onSave={handleSaveForTemplate(entry.template.id)}
                          onCancel={handleCancelForTemplate(entry.template.id)}
                        />
                      ))}
                    </div>
                  </div>
                )}
                {showCreationCards && activeGoalsFiltered.length > 0 && (
                  <div className='relative flex items-center py-1'>
                    <div className='flex-grow border-t border-border' />
                    <span className='mx-3 text-xs text-muted-foreground whitespace-nowrap'>
                      Existing goals
                    </span>
                    <div className='flex-grow border-t border-border' />
                  </div>
                )}
                {activeGoalsFiltered.length > 0 ? (
                  discipline ? (
                    <>
                      <GoalGroup
                        title={`${discipline} Goals`}
                        goals={activeGoalGroups.selected}
                        renderGoal={renderActiveGoalCard}
                      />
                      <GoalGroup
                        title='Other Disciplines'
                        goals={activeGoalGroups.other}
                        renderGoal={renderActiveGoalCard}
                      />
                    </>
                  ) : (
                    <div className='grid grid-cols-1 xl:grid-cols-2 items-start gap-2'>
                      {activeGoalsFiltered.map(renderActiveGoalCard)}
                    </div>
                  )
                ) : (
                  !showCreationCards && <EmptyState message='No goals yet' />
                )}
              </div>
            )}
          </div>
        </TabsContent>
        <TabsContent
          value='met'
          className='data-[state=active]:flex flex-col data-[state=active]:flex-1 min-h-0'
        >
          <div className='overflow-y-auto flex-1 pr-2 [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden'>
            {isLoading ? (
              <div className='flex items-center justify-center py-12 text-muted-foreground'>
                <Loader2 className='h-6 w-6 animate-spin' />
              </div>
            ) : metGoals.length > 0 ? (
              <div className='grid grid-cols-1 xl:grid-cols-2 items-start gap-2'>
                {metGoals.map(goal => (
                  <DisplayGoalCard
                    key={goal.id}
                    goal={goal}
                    visitId={visit.id}
                    canTrackProgress={capabilities.canRecordProgress}
                    canAddInterventions={capabilities.canAddInterventions}
                    canMarkGoalOutcome={capabilities.canMarkGoalOutcome}
                    canEditGoal={capabilities.canEditGoals}
                    canDeleteGoal={capabilities.canDeleteGoals}
                    canPushGoal={capabilities.canPushGoals}
                    canPerformInterventions={
                      capabilities.canPerformInterventions
                    }
                    canCompleteInterventions={
                      capabilities.canCompleteInterventions
                    }
                    canDeleteInterventions={capabilities.canDeleteGoals}
                    isViewOnly={true}
                    episodeStartDate={visit.episode_start_date}
                    episodeEndDate={visit.episode_end_date}
                    onGoalProgressSaved={handleGoalProgressSaved}
                  />
                ))}
              </div>
            ) : (
              <EmptyState message='No met goals for this patient' />
            )}
          </div>
        </TabsContent>
        <TabsContent
          value='unattainable'
          className='data-[state=active]:flex flex-col data-[state=active]:flex-1 min-h-0'
        >
          <div className='overflow-y-auto flex-1 pr-2 [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden'>
            {isLoading ? (
              <div className='flex items-center justify-center py-12 text-muted-foreground'>
                <Loader2 className='h-6 w-6 animate-spin' />
              </div>
            ) : unattainableGoals.length > 0 ? (
              <div className='grid grid-cols-1 xl:grid-cols-2 items-start gap-2'>
                {unattainableGoals.map(goal => (
                  <DisplayGoalCard
                    key={goal.id}
                    goal={goal}
                    visitId={visit.id}
                    canTrackProgress={capabilities.canRecordProgress}
                    canAddInterventions={capabilities.canAddInterventions}
                    canMarkGoalOutcome={capabilities.canMarkGoalOutcome}
                    canEditGoal={capabilities.canEditGoals}
                    canDeleteGoal={capabilities.canDeleteGoals}
                    canPushGoal={capabilities.canPushGoals}
                    canPerformInterventions={
                      capabilities.canPerformInterventions
                    }
                    canCompleteInterventions={
                      capabilities.canCompleteInterventions
                    }
                    canDeleteInterventions={capabilities.canDeleteGoals}
                    isViewOnly={true}
                    episodeStartDate={visit.episode_start_date}
                    episodeEndDate={visit.episode_end_date}
                    onGoalProgressSaved={handleGoalProgressSaved}
                  />
                ))}
              </div>
            ) : (
              <EmptyState message='No unattainable goals for this patient' />
            )}
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );

  if (inline) {
    return (
      <>
        {content}
        <AddGoalsBox
          open={isAddGoalsOpen}
          onClose={handleCloseAddGoals}
          visitId={visit.id}
          discipline={discipline}
          onAddGoals={handleAddGoals}
          onUseFreeText={handleUseFreeText}
        />
      </>
    );
  }

  return (
    <>
      <Dialog open={open} onOpenChange={onClose}>
        <DialogContent className='w-[calc(100vw-2rem)] sm:max-w-lg h-[75vh] flex flex-col overflow-hidden'>
          {content}
        </DialogContent>
      </Dialog>
      <AddGoalsBox
        open={isAddGoalsOpen}
        onClose={handleCloseAddGoals}
        visitId={visit.id}
        onAddGoals={handleAddGoals}
        onUseFreeText={handleUseFreeText}
      />
    </>
  );
};

export default GoalsTab;
